import * as THREE from 'three';
import type { BattleState, PlunderCache } from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { facingToRotY, WORLD_PER_HEX } from './coords.ts';
import type { PuzzleFx } from './puzzles.ts';

/*
 * Plunder (docs/MISIONES.md): the old garrison's caches across the clearing and what is loaded on the cart.
 *
 * - A chest: dark oak bound in iron, a red wax seal on the lock; a coffer: longer and taller, three bands, two
 *   ring handles, the garrison's red stripe across the lid.
 * - Lying free, each throws a faint red glow so it reads at a glance; one a dragger hauls drags along the ground
 *   behind it, tilted, scraping dust.
 * - Carried: a chest rides over its carrier's head; a coffer hangs low between its two bearers.
 * - Loaded: a stack beside the cart in the middle grows with what made it there.
 */

const K = WORLD_PER_HEX / 1000;
const RED = new THREE.Color('#ff4f3a');
const PALE = new THREE.Color('#ff8a6e');
const OAK = '#3a2a20';
const OAK_D = '#2b1f18';
const IRON = '#34343a';
const SEAL = '#8e1f1c';
const STRIPE = '#7a2422';
/**
 * The first Vigil's chests (La Quemadura 2): the same shapes, the oak gone black in the fire, the iron dark, and an
 * open eye carved in pale bone where the garrison's red seal would be.
 */
const VIGIL = { oak: '#241b17', oakD: '#1a1412', iron: '#2b2a2e', stripe: '#3a1a18' };
const EYE = '#d6cfc2';
const IRIS = '#a8302a';
interface Wood { oak: string; oakD: string; iron: string; stripe: string; vigil: boolean }
const woodOf = (vigil: boolean): Wood => (vigil ? { ...VIGIL, vigil } : { oak: OAK, oakD: OAK_D, iron: IRON, stripe: STRIPE, vigil });

/** The lock's mark at (y, z): the garrison's red wax seal, or the first Vigil's open eye. */
const mark = (w: Wood, y: number, z: number, r: number): THREE.BufferGeometry[] =>
  w.vigil
    ? [
        paint(new THREE.SphereGeometry(r * 1.6, 8, 4).scale(1.6, 0.8, 0.35).translate(0, y, z), EYE),
        paint(new THREE.SphereGeometry(r * 0.75, 6, 4).scale(1, 1, 0.4).translate(0, y, z + r * 0.45), IRIS),
      ]
    : [paint(new THREE.SphereGeometry(r, 6, 4).translate(0, y, z), SEAL)];

const paint = (g: THREE.BufferGeometry, color: string): THREE.BufferGeometry => {
  const n = g.toNonIndexed();
  n.deleteAttribute('uv');
  const c = new THREE.Color(color);
  const cnt = n.getAttribute('position').count;
  const a = new Float32Array(cnt * 3);
  for (let i = 0; i < cnt; i++) a.set([c.r, c.g, c.b], i * 3);
  n.setAttribute('color', new THREE.Float32BufferAttribute(a, 3));
  return n;
};
const box = (w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(x, y, z);

/** A chest about 0.32 wide, its base at y = 0. */
const chestGeo = (wd: Wood): THREE.BufferGeometry => {
  const w = 0.32, h = 0.2, d = 0.22;
  const parts = [
    paint(box(w, h * 0.72, d, 0, h * 0.36, 0), wd.oak),
    paint(new THREE.CylinderGeometry(d / 2, d / 2, w, 8, 1, false, 0, Math.PI).rotateZ(Math.PI / 2).rotateX(Math.PI / 2).translate(0, h * 0.72, 0), wd.oakD),
    paint(box(0.02, h * 0.74 + 0.005, d + 0.012, -w * 0.32, h * 0.37, 0), wd.iron),
    paint(box(0.02, h * 0.74 + 0.005, d + 0.012, w * 0.32, h * 0.37, 0), wd.iron),
    paint(box(0.06, 0.06, 0.02, 0, h * 0.62, d / 2 + 0.01), wd.iron),
    ...mark(wd, h * 0.58, d / 2 + 0.025, 0.022),
  ];
  return mergeGeometries(parts) as THREE.BufferGeometry;
};

/** A coffer about 0.56 long: three bands, ring handles at the ends, a red stripe across the lid. */
const cofferGeo = (wd: Wood): THREE.BufferGeometry => {
  const w = 0.56, h = 0.28, d = 0.3;
  const parts = [
    paint(box(w, h * 0.7, d, 0, h * 0.35, 0), wd.oak),
    paint(box(w + 0.01, h * 0.22, d + 0.01, 0, h * 0.81, 0), wd.oakD),
    paint(box(w + 0.015, 0.03, 0.06, 0, h * 0.93, 0), wd.stripe),
  ];
  for (const x of [-0.36, 0, 0.36]) parts.push(paint(box(0.022, h * 0.94, d + 0.014, x * w, h * 0.47, 0), wd.iron));
  for (const k of [-1, 1]) parts.push(paint(new THREE.TorusGeometry(0.04, 0.01, 4, 8).rotateY(Math.PI / 2).translate(k * (w / 2 + 0.012), h * 0.55, 0), wd.iron));
  parts.push(...mark(wd, h * 0.6, d / 2 + 0.02, 0.028));
  return mergeGeometries(parts) as THREE.BufferGeometry;
};

export class PlunderLayer {
  readonly group = new THREE.Group();
  private mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.15, flatShading: true });
  private chestG: THREE.BufferGeometry;
  private cofferG: THREE.BufferGeometry;
  private meshes = new Map<number, THREE.Mesh>();
  /** The stack by the cart: one mesh per loaded worth, up to a dozen. */
  private stack: THREE.Mesh[] = [];

  constructor(
    private ground: (x: number, z: number) => number,
    private fx: PuzzleFx,
    /** La Quemadura 2: the first Vigil's chests instead of the garrison's. */
    vigil = false,
  ) {
    const wd = woodOf(vigil);
    this.chestG = chestGeo(wd);
    this.cofferG = cofferGeo(wd);
    this.group.name = 'plunder';
    for (let i = 0; i < 12; i++) {
      const m = new THREE.Mesh(this.chestG, this.mat);
      m.scale.setScalar(1.4);
      m.visible = false;
      m.castShadow = true;
      this.stack.push(m);
      this.group.add(m);
    }
  }

  private mesh(c: PlunderCache): THREE.Mesh {
    let m = this.meshes.get(c.id);
    if (!m) {
      m = new THREE.Mesh(c.kind === 'coffer' ? this.cofferG : this.chestG, this.mat);
      m.name = `cache:${c.kind}`;
      // Bigger than life, as every miniature on the table: it has to read from the game camera.
      m.scale.setScalar(1.6);
      m.castShadow = true;
      this.meshes.set(c.id, m);
      this.group.add(m);
    }
    return m;
  }

  sync(s: BattleState, fresh: boolean, pos: (id: number) => THREE.Vector3 | undefined, cart: THREE.Vector3): void {
    const p = s.plunder;
    if (!p) return;
    const tick = s.tick;
    const seen = new Set<number>();
    for (const c of p.caches) {
      seen.add(c.id);
      const m = this.mesh(c);
      m.rotation.set(0, 0, 0);
      const carriers = c.carriers.map((id) => ({ p: pos(id), h: s.heroes.find((h) => h.id === id) })).filter((x) => x.p && x.h);
      const floor = new THREE.Vector3(c.x * K, 0, c.y * K);
      floor.y = this.ground(floor.x, floor.z);
      if (c.kind === 'coffer' && carriers.length >= 2) {
        const a = carriers[0]!.p!;
        const b = carriers[1]!.p!;
        m.position.copy(a).lerp(b, 0.5).setY(Math.max(a.y, b.y) + 0.34 + Math.sin(tick * 0.6) * 0.02);
        m.rotation.y = Math.atan2(b.x - a.x, b.z - a.z) + Math.PI / 2;
      } else if (c.kind === 'chest' && carriers[0]) {
        const yaw = facingToRotY(carriers[0].h!.facing);
        m.position.copy(carriers[0].p!).add(new THREE.Vector3(Math.sin(yaw) * 0.08, 1.0, Math.cos(yaw) * 0.08));
        m.rotation.y = yaw;
      } else {
        m.position.copy(floor);
        m.rotation.y = (c.id * 1.7) % (Math.PI * 2);
        if (c.dragger) {
          // Hauled off: dragging on one edge, scraping dust.
          m.rotation.z = 0.35;
          if (fresh && tick % 3 === 0) this.fx.vfx.burst(floor.clone().setY(floor.y + 0.05), { n: 1, color: '#5a4a3c', speed: 0.01, up: 0.012, size: 0.2, life: 10, smoke: true });
          this.fx.glow.add(floor.x, floor.y + 0.25, floor.z, RED, 0.9, 0.35 + 0.15 * Math.sin(tick * 0.9));
        } else if (!carriers.length) {
          // Lying free: a faint red glow so it reads from afar.
          this.fx.glow.add(floor.x, floor.y + 0.3, floor.z, c.kind === 'coffer' ? PALE : RED, c.kind === 'coffer' ? 1.4 : 1.1, 0.26 + 0.08 * Math.sin(tick * 0.3 + c.id));
        }
      }
    }
    for (const [id, m] of this.meshes) {
      if (seen.has(id)) continue;
      // Loaded on the cart, or hauled down a hole.
      this.group.remove(m);
      this.meshes.delete(id);
    }
    // The stack beside the cart: two by two, rising.
    const n = Math.min(this.stack.length, p.loaded);
    this.stack.forEach((m, i) => {
      m.visible = i < n;
      if (!m.visible) return;
      const row = Math.floor(i / 4), k = i % 4;
      const x = cart.x + 0.95 + (k % 2) * 0.46, z = cart.z - 0.6 + Math.floor(k / 2) * 0.34;
      m.position.set(x, this.ground(x, z) + row * 0.2, z);
      m.rotation.y = 0.4 + row * 0.15;
    });
  }

  dispose(): void {
    this.chestG.dispose();
    this.cofferG.dispose();
    this.mat.dispose();
  }
}
